import type { PureQueryOptions } from '@apollo/client';
import { gql } from '~/apollo/client-v3';
import { useQuery } from '@apollo/client';
import * as R from 'ramda';
import * as fragments from '~/apollo/fragments';
import type {
  OutcropSedimentaryLogsTabQuery,
  OutcropSedimentaryLogsTabQueryVariables,
} from '~/apollo/generated/v3/graphql';
import { BookmarkParentType } from '~/apollo/generated/v3/graphql';
import { NotFound } from '~/components/common/NotFound';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { SupportingObjectList } from '~/components/supportingObject/SupportingObjectList';
import { TaggedSupportingObjectList } from '~/components/supportingObject/TaggedSupportingObjectList';
import { useOutcropOutletContext } from '~/routes/outcrop/$outcropId';

const OUTCROP_SEDIMENTARY_LOGS_TAB = gql`
  query OutcropSedimentaryLogsTab($outcropId: Int!) {
    outcropList(id: $outcropId) {
      ...outcropParts
      sedimentaryLogs {
        ...sedimentaryLogParts
        pictures {
          ...pictureParts
          file {
            ...fileParts
            signedUrl
          }
        }
        files {
          ...fileParts
          signedUrl
        }
        georeference {
          ...georeferenceParts
        }
      }

      studies {
        ...studyParts

        dataHistory {
          ...dataHistoryParts
        }

        sedimentaryLogs {
          ...sedimentaryLogParts
          pictures {
            ...pictureParts
            file {
              ...fileParts
              signedUrl
            }
          }
          files {
            ...fileParts
            signedUrl
          }
          georeference {
            ...georeferenceParts
          }
        }
      }
    }
  }

  ${fragments.outcropParts}
  ${fragments.sedimentaryLogParts}
  ${fragments.pictureParts}
  ${fragments.fileParts}
  ${fragments.studyParts}
  ${fragments.dataHistoryParts}
  ${fragments.georeferenceParts}
`;

export default function OutcropSedimentaryLogsRoute() {
  const ctx = useOutcropOutletContext();
  const outcropId = ctx.outcrop.id;

  const { data, loading } = useQuery<
    OutcropSedimentaryLogsTabQuery,
    OutcropSedimentaryLogsTabQueryVariables
  >(OUTCROP_SEDIMENTARY_LOGS_TAB, { variables: { outcropId } });

  const refetchQueries: PureQueryOptions[] = [
    { query: OUTCROP_SEDIMENTARY_LOGS_TAB, variables: { outcropId } },
  ];

  const outcrop = data?.outcropList.find(oc => oc.id === outcropId);
  const sedLogs = outcrop?.sedimentaryLogs ?? [];
  const outcropStudies = R.sortBy(R.prop('name'), outcrop?.studies ?? []);

  if (loading) return <SpinnerPlaceholder />;
  if (!outcrop) return <NotFound />;

  return (
    <>
      <SupportingObjectList
        items={sedLogs}
        soParentType="outcrop"
        soParentId={outcropId}
        bookmarkParentType={BookmarkParentType.SedimentaryLog}
        fade
        refetchQueries={refetchQueries}
      />

      <TaggedSupportingObjectList
        outcropId={outcropId}
        studies={outcropStudies}
        supportObjectKey="sedimentaryLogs"
        fade
        refetchQueries={refetchQueries}
      />
    </>
  );
}
